import { useEffect, useRef, useState } from 'react';
import { X, Camera, Loader2, CheckCircle2, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { apiService, UploadedFoto } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';

/**
 * Um passo de nudge. 1/30s cobre o caso comum (celular grava a 30 fps): o
 * técnico usa a linha do tempo para chegar perto e estes botões para parar
 * exatamente no quadro em que a proteção aparece.
 */
const PASSO_QUADRO = 1 / 30;

interface Props {
  projetoId: string;
  machineFolder: string;
  foto: UploadedFoto;
  /** URL absoluta do vídeo (a página resolve conforme o ambiente). */
  videoUrl: string;
  onFechar: () => void;
  /** Dispara quando um quadro vira foto da pasta, para a grade recarregar. */
  onQuadroExtraido: (novaFoto: UploadedFoto) => void;
}

function formatarTempo(segundos: number): string {
  if (!Number.isFinite(segundos)) return '0:00.0';
  const min = Math.floor(segundos / 60);
  const seg = segundos % 60;
  return `${min}:${seg.toFixed(1).padStart(4, '0')}`;
}

export function VisualizadorVideoModal({
  projetoId,
  machineFolder,
  foto,
  videoUrl,
  onFechar,
  onQuadroExtraido,
}: Props) {
  const { t } = useI18n();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [tempoAtual, setTempoAtual] = useState(0);
  const [duracao, setDuracao] = useState(0);
  const [extraindo, setExtraindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [capturados, setCapturados] = useState<UploadedFoto[]>([]);
  const [semSuporte, setSemSuporte] = useState(false);

  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onFechar]);

  const andar = (delta: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = Math.min(Math.max(video.currentTime + delta, 0), duracao || video.duration || 0);
  };

  const capturarQuadro = async () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();

    setExtraindo(true);
    setErro(null);
    try {
      const resposta = await apiService.extrairFrameDeVideo(
        projetoId,
        machineFolder,
        foto.nomeArquivo,
        video.currentTime
      );
      if (!resposta.success || !resposta.data) {
        setErro(resposta.message || t('imagens.acervo.video.erroExtrair'));
        return;
      }
      setCapturados((prev) => [resposta.data as UploadedFoto, ...prev]);
      onQuadroExtraido(resposta.data);
    } catch (e) {
      setErro(e instanceof Error ? e.message : t('imagens.acervo.video.erroExtrairGenerico'));
    } finally {
      setExtraindo(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 text-white shrink-0">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{foto.nomeArquivo}</p>
          <p className="text-xs text-white/60 truncate">{machineFolder.replace(/_/g, ' ')}</p>
        </div>
        <button
          type="button"
          onClick={onFechar}
          className="p-2 rounded bg-white/20 shrink-0"
          title={t('imagens.acervo.video.fechar')}
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center px-4">
        {semSuporte ? (
          <div className="text-center text-white/80 max-w-md">
            <AlertCircle className="h-10 w-10 mx-auto mb-3 text-amber-400" />
            <p className="text-sm">{t('imagens.acervo.video.semSuporte')}</p>
          </div>
        ) : (
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            preload="metadata"
            className="max-w-full max-h-full"
            onTimeUpdate={(e) => setTempoAtual(e.currentTarget.currentTime)}
            onLoadedMetadata={(e) => setDuracao(e.currentTarget.duration)}
            onError={() => setSemSuporte(true)}
          />
        )}
      </div>

      <div className="shrink-0 bg-black/60 border-t border-white/10 px-4 py-3">
        {erro && (
          <div className="mb-3 flex items-center gap-2 text-sm text-red-300">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{erro}</span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => andar(-PASSO_QUADRO)}
              className="p-2 rounded bg-white/15 text-white hover:bg-white/25"
              title={t('imagens.acervo.video.quadroAnterior')}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="font-mono text-sm text-white tabular-nums w-28 text-center">
              {formatarTempo(tempoAtual)}
              <span className="text-white/50"> / {formatarTempo(duracao)}</span>
            </span>
            <button
              type="button"
              onClick={() => andar(PASSO_QUADRO)}
              className="p-2 rounded bg-white/15 text-white hover:bg-white/25"
              title={t('imagens.acervo.video.quadroProximo')}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <button
            type="button"
            onClick={capturarQuadro}
            disabled={extraindo}
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-schmersal-blue text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {extraindo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            {extraindo ? t('imagens.acervo.video.extraindo') : t('imagens.acervo.video.capturar')}
          </button>

          <p className="text-xs text-white/50">{t('imagens.acervo.video.dica')}</p>
        </div>

        {capturados.length > 0 && (
          <div className="mt-3 flex items-center gap-2 overflow-x-auto">
            <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0" />
            <span className="text-xs text-white/70 shrink-0">
              {t('imagens.acervo.video.salvos', { quantidade: capturados.length })}
            </span>
            {capturados.map((c) => (
              <span
                key={c.nomeArquivo}
                className="text-xs text-white/90 bg-white/10 rounded px-2 py-1 whitespace-nowrap"
              >
                {c.nomeArquivo}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
